import { useMutation, useQuery } from '@tanstack/react-query';
import { fetch3, fetch4, fetch5 } from '../commonApis/commonApis';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// API response types
export interface Picklist {
  id: string;
  clientId: string;
  picklistNumber: string;
  type: 'SINGLE_ORDER' | 'BATCH_ORDER';
  status: 'GENERATED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ASSIGNED' | 'PICKED';
  warehouseId?: string;
  createdBy: string;
  assignedTo?: string;
  notes?: string;
  generatedAt: string;
  startedAt?: string;
  completedAt?: string;
  totalOrders: number;
  completedOrders: number;
  createdAt: string;
  updatedAt: string;
  orders?: Order[];
  picklistOrders?: any[];
  client?: Client;
  creator?: Staff;
  assignee?: Staff;
}

export interface Order {
  id: string;
  awbNumber?: string;
  referenceNumber: string;
  status: string;
}

export interface Client {
  id: string;
  name: string;
  email: string;
}

export interface Staff {
  id: string;
  email: string;
  details?: {
    id: string;
    fullName: string;
  };
}

export interface GeneratePicklistRequest {
  type: 'SINGLE_ORDER' | 'BATCH_ORDER';
  orderIds: string[];
  clientId: string;
  warehouseId?: string;
  notes?: string;
  assignedTo?: string;
  updateToPicked?: boolean;
}

export interface GeneratePicklistResponse {
  success: boolean;
  message: string;
  data?: Picklist;
  errors?: string[];
}

export interface PicklistsResponse {
  success: boolean;
  data: Picklist[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  statusCounts?: {
    generated: number;
    in_progress: number;
    completed: number;
  };
}

export interface PicklistResponse {
  success: boolean;
  data: Picklist;
}

// API Functions
export const picklistApi = {
  // Generate picklist
  generatePicklist: async (data: GeneratePicklistRequest): Promise<GeneratePicklistResponse> => {
    return await fetch4(`${API_BASE_URL}/api/picklists/generate`, data);
  },

  // Get all picklists
  getPicklists: async (params?: {
    page?: number;
    limit?: number;
    type?: 'SINGLE_ORDER' | 'BATCH_ORDER';
    status?: 'GENERATED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ASSIGNED' | 'PICKED';
    warehouseId?: string;
    search?: string;
    searchField?: string;
  }): Promise<PicklistsResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.warehouseId) queryParams.append('warehouseId', params.warehouseId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.searchField) queryParams.append('searchField', params.searchField);

    return await fetch3(`${API_BASE_URL}/api/picklists?${queryParams}`, 'get');
  },

  // Get picklist by ID
  getPicklistById: async (id: string): Promise<PicklistResponse> => {
    return await fetch3(`${API_BASE_URL}/api/picklists/${id}`, 'get');
  },

  // Update picklist status
  updatePicklistStatus: async (id: string, status: 'GENERATED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ASSIGNED' | 'PICKED'): Promise<{ success: boolean; message: string }> => {
    return await fetch5(`${API_BASE_URL}/api/picklists/${id}/status`, 'put', { status });
  },

  // Assign picklist to staff
  assignPicklist: async (id: string, assignedTo: string): Promise<{ success: boolean; message: string }> => {
    return await fetch5(`${API_BASE_URL}/api/picklists/${id}/assign`, 'put', { assignedTo });
  },

  // Delete picklist
  deletePicklist: async (id: string): Promise<{ success: boolean; message: string }> => {
    return await fetch5(`${API_BASE_URL}/api/picklists/${id}`, 'delete');
  },
};

// React Query Hooks
export const useGeneratePicklistMutation = () => {
  return useMutation({
    mutationFn: async (data: GeneratePicklistRequest) => {
      const res: GeneratePicklistResponse = await picklistApi.generatePicklist(data);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Failed to generate picklist';
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ Picklist generated successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Picklist generation failed:', error.message);
    },
  });
};
export const usePicklistsQuery = (params?: {
  page?: number;
  limit?: number;
  type?: 'SINGLE_ORDER' | 'BATCH_ORDER';
  status?: 'GENERATED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ASSIGNED' | 'PICKED';
  warehouseId?: string;
  search?: string;
  searchField?: string;
}) => {
  return useQuery({
    queryKey: ['picklists', params],
    queryFn: async () => {
      const res: PicklistsResponse = await picklistApi.getPicklists(params);
      return res;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePicklistByIdQuery = (id: string) => {
  return useQuery({
    queryKey: ['picklist', id],
    queryFn: async () => {
      const res: PicklistResponse = await picklistApi.getPicklistById(id);
      return res?.data ?? null;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdatePicklistStatusMutation = () => {
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'GENERATED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ASSIGNED' | 'PICKED' }) => {
      const res: { success: boolean; message: string } = await picklistApi.updatePicklistStatus(id, status);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Failed to update picklist status';
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log('✅ Picklist status updated successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Picklist status update failed:', error.message);
    },
  });
};

export const useAssignPicklistMutation = () => {
  return useMutation({
    mutationFn: async ({ id, assignedTo }: { id: string; assignedTo: string }) => {
      const res: { success: boolean; message: string } = await picklistApi.assignPicklist(id, assignedTo);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Failed to assign picklist';
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log('✅ Picklist assigned successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Picklist assignment failed:', error.message);
    },
  });
};

export const useDeletePicklistMutation = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res: { success: boolean; message: string } = await picklistApi.deletePicklist(id);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Failed to delete picklist';
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log('✅ Picklist deleted successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Picklist deletion failed:', error.message);
    },
  });
};
